// src/lib/platformActivity.js
// Merges CRM timeline + product collections. Uses multiple user-id and timestamp fields because
// Scanbrix collections vary (uid vs userId, createdAt vs updatedAt, etc.).
import { collection, query, where, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { watchActivity, watchGlobalActivity, watchSubtimeline, watchDealActivity } from "./db";
import { formatWorkspaceAddress } from "./workspaceAddress";
import {
  activityTypeForProductEvent,
  buildProductEventActivityText,
  isIosProductAnalyticsEvent,
} from "./productAnalyticsEvents";

import { coerceTimestampMs, ga4DateStartMs } from "./timestampCoerce";

function timeMs(t) {
  const ms = coerceTimestampMs(t);
  return ms == null ? 0 : ms;
}

function pickTime(data, fields) {
  for (const k of fields) {
    const v = data[k];
    if (v != null) return v;
  }
  return null;
}

/**
 * GA4 backfill rows only have a calendar date; `created_at` is anchored at noon UTC and can
 * appear in the future (shows as "just now" for every event). Prefer start-of-day in GA4 TZ.
 */
function resolvePlatformEventTime(data, picked, source) {
  if (source.id !== "product_analytics_events") return picked;
  const ga4Date = data.ga4_date ?? data.ga4Date;
  if (!ga4Date) return picked;

  const ga4Ms = ga4DateStartMs(ga4Date);
  if (ga4Ms == null) return picked;

  const isBackfill = data.backfill === true || data.source === "backfill";
  const pickedMs = timeMs(picked);

  if (isBackfill || pickedMs <= 0 || pickedMs > Date.now()) {
    return new Date(ga4Ms);
  }
  return picked;
}

function getByPath(data, path) {
  if (!data || typeof path !== "string") return undefined;
  const parts = path.split(".");
  let cur = data;
  for (const p of parts) {
    cur = cur?.[p];
  }
  return cur;
}

/** String uid/email, or Firestore DocumentReference → document id. */
function unwrapUserLink(v) {
  if (v == null || v === "") return null;
  if (typeof v === "string") {
    const s = v.trim();
    return s || null;
  }
  if (typeof v === "object" && typeof v.id === "string") {
    if (typeof v.path === "string" && v.path.length > 0) return v.id;
    if (v.firestore && v.id) return v.id;
  }
  if (typeof v === "object" && typeof v.email === "string") {
    const e = v.email.trim();
    if (e.includes("@")) return e;
  }
  if (typeof v === "object" && v.uid != null) {
    const u = String(v.uid).trim();
    if (u) return u;
  }
  return null;
}

function pickUserId(data, fields) {
  for (const f of fields) {
    const v = f.includes(".") ? getByPath(data, f) : data[f];
    const u = unwrapUserLink(v);
    if (u) return u;
  }
  return null;
}

/** Scanbrix production schema (e.g. orders) uses snake_case: user_id, created_at, updated_at. */
const DEFAULT_USER_LINK_FIELDS = [
  "user_id",
  "userId",
  "userID",
  "uid",
  "captured_by",
  "capturedBy",
  "firebase_uid",
  "firebaseUid",
  "owner_id",
  "ownerId",
  "owner_user_id", // Scanbrix `workspaces` collection
  "creatorId",
  "ownerUserId",
  "userUuid",
  "account_id",
  "created_by_user_id",
  "workspace_owner_id",
  "customerId",
  "createdBy",
  "created_by",
  "uploadedBy",
  "memberId",
  "payerId",
  "submittedBy",
  "assignedTo",
  "actorId",
  "performedBy",
];

/** Nested / alternate shapes (owner object, metadata map, …). */
const DEFAULT_USER_LINK_PATHS = [
  "owner.uid",
  "owner.id",
  "owner.userId",
  "owner.user_id",
  "owner.email",
  "user.uid",
  "user.id",
  "user.userId",
  "user.user_id",
  "user.email",
  "creator.uid",
  "creator.id",
  "creator.email",
  "metadata.userId",
  "metadata.user_id",
  "metadata.uid",
  "metadata.ownerId",
  "metadata.email",
];

const DEFAULT_TIME_ORDER_FIELDS = [
  "created_at",
  "updated_at",
  "completed_at",
  "delivered_at",
  "started_at",
  "createdAt",
  "updatedAt",
  "timestamp",
  "placedAt",
  "completedAt",
  "uploadedAt",
  "joinedAt",
  "paidAt",
  "startedAt",
];

function parseListEnv(raw) {
  if (!raw || typeof raw !== "string") return null;
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : null;
}

function envUserFields(key) {
  return parseListEnv(process.env[key]) || DEFAULT_USER_LINK_FIELDS;
}

function envTimeFields(key) {
  return parseListEnv(process.env[key]) || DEFAULT_TIME_ORDER_FIELDS;
}

/**
 * userFields: equality filters OR-merged (multiple listeners, union by doc id).
 * orderFields: tried in order for dashboard “recent” queries before limit-only fallback.
 */
export const PLATFORM_SOURCES = [
  {
    id: "orders",
    collection: "orders",
    userFields: envUserFields("REACT_APP_ACTIVITY_ORDERS_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_ORDERS_TIME_FIELDS"),
    activityType: "order_created",
    buildText: (d) => {
      const st = d.status ? String(d.status) : "Order";
      const sym = (d.currency_symbol != null ? d.currency_symbol : d.currency) || "$";
      const price = d.final_price ?? d.estimated_price ?? d.total ?? d.amount;
      const money = price != null ? ` · ${sym}${Number(price).toLocaleString()}` : "";
      const oid = d.order_id ? ` #${String(d.order_id).replace(/-/g, "").slice(0, 8)}` : "";
      const ws = d.workspace_name ? ` · ${d.workspace_name}` : "";
      const ft = d.file_type || (Array.isArray(d.file_types) && d.file_types[0]) || "";
      const ftPart = ft ? ` · ${ft}` : "";
      return `${st}${oid}${money}${ws}${ftPart}`;
    },
  },
  {
    id: "product_analytics_events",
    collection: "product_analytics_events",
    userFields:
      parseListEnv(process.env.REACT_APP_ACTIVITY_SCAN_EVENTS_USER_FIELDS) ||
      ["uid", "user_id", "userId"],
    orderFields:
      envTimeFields("REACT_APP_ACTIVITY_SCAN_EVENTS_TIME_FIELDS") ||
      ["created_at", "createdAt", "timestamp"],
    activityType: "product_analytics_event",
    activityTypeFromDoc: (d) => activityTypeForProductEvent(d?.event_name ?? d?.eventName),
    includeDoc: (d) => isIosProductAnalyticsEvent(d),
    buildText: (d) => buildProductEventActivityText(d),
  },
  {
    id: "scans",
    collection: "scans",
    userFields:
      parseListEnv(process.env.REACT_APP_ACTIVITY_SCANS_USER_FIELDS) ||
      ["captured_by", "capturedBy", "created_by", "createdBy", "user_id", "userId", "uploadedBy", "uid"],
    orderFields: envTimeFields("REACT_APP_ACTIVITY_SCANS_TIME_FIELDS"),
    activityType: "scan_started",
    buildText: (d) => {
      const ws = d.workspace_name ?? d.workspaceName;
      const name = d.scan_name || d.name || d.title || d.fileName;
      const suffix = ws
        ? ` · ${String(ws).trim()}`
        : name
          ? ` · ${String(name).trim()}`
          : "";
      return `Started scanning${suffix}`;
    },
  },
  {
    id: "workspaces",
    collection: "workspaces",
    userFields: envUserFields("REACT_APP_ACTIVITY_WORKSPACES_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_WORKSPACES_TIME_FIELDS"),
    activityType: "workspace_created",
    buildText: (d) => {
      const name = d.name || d.title || d.workspace_name;
      const base = name ? `Workspace · ${name}` : "Workspace created";
      const addr = formatWorkspaceAddress(d);
      return addr ? `${base} · ${addr}` : base;
    },
  },
  {
    id: "workspace_members",
    collection: "workspace_members",
    userFields: envUserFields("REACT_APP_ACTIVITY_WORKSPACE_MEMBERS_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_WORKSPACE_MEMBERS_TIME_FIELDS"),
    activityType: "workspace_joined",
    buildText: (d) => {
      const wid = d.workspace_id || d.workspaceId;
      return wid ? `Workspace member · ${String(wid).slice(0, 10)}…` : "Workspace membership";
    },
  },
  {
    id: "payments",
    collection: "payments",
    userFields: envUserFields("REACT_APP_ACTIVITY_PAYMENTS_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_PAYMENTS_TIME_FIELDS"),
    activityType: "stripe_payment",
    buildText: (d) => {
      const sym = d.currency_symbol || "$";
      const amt = d.amount ?? d.final_price ?? d.estimated_price;
      const money = amt != null ? `${sym}${Number(amt).toLocaleString()}` : "Payment";
      const st = d.status ? ` · ${d.status}` : "";
      return `Payment ${money}${st}`;
    },
  },
  {
    id: "conversion_jobs",
    collection: "conversion_jobs",
    userFields: envUserFields("REACT_APP_ACTIVITY_CONVERSION_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_CONVERSION_TIME_FIELDS"),
    activityType: "conversion_job",
    buildText: (d) =>
      d.status ? `Conversion · ${d.status}` : d.type ? `Conversion · ${d.type}` : "Conversion job",
  },
  {
    id: "files",
    collection: "files",
    userFields: envUserFields("REACT_APP_ACTIVITY_FILES_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_FILES_TIME_FIELDS"),
    activityType: "file_uploaded",
    buildText: (d) =>
      d.name || d.fileName || d.path
        ? `File · ${d.name || d.fileName || d.path}`
        : "File uploaded",
  },
  {
    id: "operator_jobs",
    collection: "operator_jobs",
    userFields: envUserFields("REACT_APP_ACTIVITY_OPERATOR_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_OPERATOR_TIME_FIELDS"),
    activityType: "operator_job",
    buildText: (d) =>
      d.status ? `Operator job · ${d.status}` : d.type ? `Operator job · ${d.type}` : "Operator job update",
  },
  {
    id: "job_audit_logs",
    collection: "job_audit_logs",
    userFields: envUserFields("REACT_APP_ACTIVITY_AUDIT_USER_FIELDS"),
    orderFields: envTimeFields("REACT_APP_ACTIVITY_AUDIT_TIME_FIELDS"),
    activityType: "audit_log",
    buildText: (d) => d.action || d.message || d.detail || "Job audit entry",
  },
];

function uniqKeys(keys) {
  const seen = new Set();
  return keys.filter((k) => {
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Display name from product doc fields (workspace owner, etc.) when present. */
export function pickActorNameFromDoc(d) {
  if (!d || typeof d !== "object") return null;
  const fn = d.first_name || d.firstName;
  const ln = d.last_name || d.lastName;
  const joined = [fn, ln].filter(Boolean).join(" ").trim();
  const nested = [d.owner, d.user, d.creator, d.createdBy].filter((x) => x && typeof x === "object");
  const nestedNames = nested.flatMap((o) => [
    o.displayName,
    o.name,
    o.fullName,
    o.full_name,
    o.userName,
    o.user_name,
    o.email ? String(o.email).split("@")[0] : null,
  ]);
  const candidates = [
    ...nestedNames,
    d.displayName,
    d.name,
    d.full_name,
    d.fullName,
    d.user_name,
    d.userName,
    d.owner_name,
    d.ownerName,
    d.created_by_name,
    d.creator_name,
    d.user_display_name,
    d.profile_name,
    joined,
    d.email ? String(d.email).split("@")[0] : null,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
}

function isLikelyFirebaseUid(s) {
  if (!s || typeof s !== "string") return false;
  return /^[A-Za-z0-9]{20,36}$/.test(s.trim());
}

/** First uid/email from arrays of ids or member objects (common workspace shapes). */
function pickUserIdFromWorkspaceArrays(d) {
  if (!d || typeof d !== "object") return null;
  const idArrayKeys = [
    "ownerIds",
    "owner_ids",
    "owners",
    "adminIds",
    "admin_ids",
    "memberIds",
    "member_ids",
    "userIds",
    "user_ids",
    "userIDs",
    "collaboratorIds",
    "memberUserIds",
  ];
  for (const k of idArrayKeys) {
    const arr = d[k];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    for (const item of arr) {
      const u = unwrapUserLink(item);
      if (u) return u;
    }
  }
  const memberArrayKeys = ["members", "workspaceMembers", "users", "collaborators", "team"];
  for (const k of memberArrayKeys) {
    const arr = d[k];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const ownerRow =
      arr.find(
        (m) =>
          m &&
          typeof m === "object" &&
          (m.role === "owner" ||
            m.role === "admin" ||
            m.role === "Admin" ||
            m.isOwner === true ||
            m.owner === true ||
            m.is_owner === true)
      ) || arr[0];
    const u = unwrapUserLink(ownerRow);
    if (u) return u;
  }
  return null;
}

/** Nested maps often used on workspace/org docs. */
function pickUserIdFromWorkspaceMaps(d) {
  if (!d || typeof d !== "object") return null;
  const paths = [
    "settings.ownerId",
    "settings.userId",
    "settings.createdBy",
    "config.ownerId",
    "config.userId",
    "preferences.ownerId",
    "organization.ownerId",
    "org.ownerId",
    "org.owner_uid",
    "billing.ownerId",
    "createdByUser.uid",
    "createdByUser.id",
  ];
  for (const p of paths) {
    const u = unwrapUserLink(getByPath(d, p));
    if (u) return u;
  }
  return null;
}

/**
 * Some apps encode creator uid in the document id (e.g. `{uid}_slug` or `{uid}`).
 * Only returns a segment that looks like a Firebase uid to limit false positives.
 */
function pickUserIdFromWorkspaceDocId(docId) {
  if (!docId || typeof docId !== "string") return null;
  const trimmed = docId.trim();
  if (isLikelyFirebaseUid(trimmed)) return trimmed;
  const parts = trimmed.split(/[_\-]/).filter(Boolean);
  for (const p of parts) {
    if (isLikelyFirebaseUid(p)) return p.trim();
  }
  return null;
}

/** `workspace_members` doc ids are often `{workspace_uuid}_{firebase_uid}`. */
function pickUserIdFromWorkspaceMemberDocId(docId) {
  if (!docId || typeof docId !== "string") return null;
  const re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_([A-Za-z0-9]{20,36})$/i;
  const m = docId.trim().match(re);
  return m ? m[1] : null;
}

function pickWorkspaceUserId(d, docId) {
  return (
    pickUserIdFromWorkspaceArrays(d) ||
    pickUserIdFromWorkspaceMaps(d) ||
    pickUserIdFromWorkspaceDocId(docId)
  );
}

/** Display name from members[] / collaborators[] when top-level doc has no profile fields. */
function pickActorNameFromWorkspaceArrays(d) {
  if (!d || typeof d !== "object") return null;
  const keys = ["members", "workspaceMembers", "users", "collaborators", "team"];
  for (const k of keys) {
    const arr = d[k];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const ownerRow =
      arr.find(
        (m) =>
          m &&
          typeof m === "object" &&
          (m.role === "owner" ||
            m.role === "admin" ||
            m.role === "Admin" ||
            m.isOwner === true ||
            m.owner === true)
      ) || arr[0];
    if (!ownerRow || typeof ownerRow !== "object") continue;
    const parts = [
      ownerRow.displayName,
      ownerRow.name,
      ownerRow.fullName,
      ownerRow.full_name,
      ownerRow.userName,
      ownerRow.username,
      ownerRow.email ? String(ownerRow.email).split("@")[0] : null,
    ];
    for (const c of parts) {
      if (c != null && String(c).trim()) return String(c).trim();
    }
  }
  return null;
}

export function docToPlatformEvent(source, docSnap) {
  const d = docSnap.data();
  const id = docSnap.id;
  const timeKeys = uniqKeys([
    ...source.orderFields,
    ...DEFAULT_TIME_ORDER_FIELDS,
  ]);
  const createdAt = resolvePlatformEventTime(d, pickTime(d, timeKeys), source);
  let uid = pickUserId(
    d,
    uniqKeys([...source.userFields, ...DEFAULT_USER_LINK_FIELDS, ...DEFAULT_USER_LINK_PATHS])
  );
  if (!uid && source.id === "workspaces") {
    uid = pickWorkspaceUserId(d, id);
  }
  if (!uid && source.id === "workspace_members") {
    uid = pickUserIdFromWorkspaceMemberDocId(id);
  }
  const actorName =
    pickActorNameFromDoc(d) ||
    (source.id === "workspaces" ? pickActorNameFromWorkspaceArrays(d) : null);
  const workspaceAddress = source.id === "workspaces" ? formatWorkspaceAddress(d) : null;
  if (source.includeDoc && !source.includeDoc(d)) return null;
  return {
    id: `${source.id}_${id}`,
    type: source.activityTypeFromDoc ? source.activityTypeFromDoc(d) : source.activityType,
    text: source.buildText({ ...d, id }),
    createdAt,
    contactId: uid || null,
    metadata: {
      collection: source.collection,
      docId: id,
      actorName: actorName || null,
      workspaceAddress: workspaceAddress || null,
      ...(d.scan_id || d.scanId ? { scanId: String(d.scan_id || d.scanId) } : {}),
      ...(d.file_id || d.fileId ? { fileId: String(d.file_id || d.fileId) } : {}),
    },
  };
}

/** Drop raw scans/files rows when product_analytics_events already mirrored the upload. */
function dedupeScanUploadActivity(rows) {
  const mirroredScans = new Set();
  const mirroredFiles = new Set();
  for (const e of rows) {
    if (e.metadata?.collection !== "product_analytics_events") continue;
    const sid = e.metadata?.scanId ?? e.metadata?.scan_id;
    const fid = e.metadata?.fileId ?? e.metadata?.file_id;
    if (sid) mirroredScans.add(String(sid));
    if (fid) mirroredFiles.add(String(fid));
  }
  if (!mirroredScans.size && !mirroredFiles.size) return rows;
  return rows.filter((e) => {
    if (e.metadata?.collection === "scans" && mirroredScans.has(String(e.metadata?.docId))) return false;
    if (e.metadata?.collection === "files" && mirroredFiles.has(String(e.metadata?.docId))) return false;
    return true;
  });
}

function mergeByTime(rows, cap) {
  const deduped = dedupeScanUploadActivity(rows);
  const withT = deduped.map((e) => ({ ...e, _t: timeMs(e.createdAt) }));
  withT.sort((a, b) => b._t - a._t);
  return withT.map(({ _t, ...r }) => r).slice(0, cap);
}

/** Subscribe to “recent” docs: try orderBy(field) until one works, else limit() + client sort by pickTime. */
function attachRecentCollectionListener(source, limitN, setRowsForSource, onSourceErr) {
  const collRef = collection(db, source.collection);
  const mapDoc = (docSnap) => docToPlatformEvent(source, docSnap);
  const mapDocs = (docs) => docs.map(mapDoc).filter(Boolean);
  let orderIdx = 0;
  let unsub = null;
  const stop = () => {
    if (unsub) {
      unsub();
      unsub = null;
    }
  };

  const runLimitOnly = () => {
    stop();
    const cap = Math.min(120, limitN * 5);
    unsub = onSnapshot(
      query(collRef, limit(cap)),
      (snap) => {
        const rows = mapDocs(snap.docs);
        rows.sort((a, b) => timeMs(b.createdAt) - timeMs(a.createdAt));
        setRowsForSource(rows.slice(0, limitN));
      },
      (err) => {
        console.error(`[recent] ${source.collection} limit() failed`, err?.code, err?.message);
        onSourceErr?.(source.id, err);
        setRowsForSource([]);
      }
    );
  };

  const tryOrder = () => {
    stop();
    if (orderIdx >= source.orderFields.length) {
      runLimitOnly();
      return;
    }
    const field = source.orderFields[orderIdx++];
    unsub = onSnapshot(
      query(collRef, orderBy(field, "desc"), limit(limitN)),
      (snap) => setRowsForSource(mapDocs(snap.docs)),
      (err) => {
        console.warn(`[recent] ${source.collection} orderBy(${field}) failed — next`, err?.code || err?.message);
        onSourceErr?.(source.id, err);
        tryOrder();
      }
    );
  };

  tryOrder();
  return stop;
}

function mergeSourceBuckets(state, source) {
  const map = new Map();
  source.userFields.forEach((field) => {
    const bucket = state.buckets[`${source.id}::${field}`];
    if (!bucket) return;
    Object.values(bucket).forEach((docSnap) => map.set(docSnap.id, docSnap));
  });
  return map;
}

/** Per-user: CRM activity + email/sequence rows + union of platform docs matching any userFields == userId. */
export function watchUserMergedTimeline(userId, callback, onError) {
  if (!userId) return () => {};

  const state = { crm: [], email: [], buckets: {} };

  const emit = () => {
    const plat = [];
    PLATFORM_SOURCES.forEach((s) => {
      mergeSourceBuckets(state, s).forEach((docSnap) => {
        const ev = docToPlatformEvent(s, docSnap);
        if (ev) plat.push(ev);
      });
    });
    callback(mergeByTime([...state.crm, ...state.email, ...plat], 140));
  };

  const unsubs = [];

  unsubs.push(
    watchActivity(
      userId,
      (rows) => {
        state.crm = rows;
        emit();
      },
      onError
    )
  );

  unsubs.push(
    watchSubtimeline(
      userId,
      "email_activity",
      (rows) => {
        state.email = rows;
        emit();
      },
      onError
    )
  );

  PLATFORM_SOURCES.forEach((s) => {
    s.userFields.forEach((field) => {
      const key = `${s.id}::${field}`;
      const q = query(collection(db, s.collection), where(field, "==", userId));
      unsubs.push(
        onSnapshot(
          q,
          (snap) => {
            const bucket = {};
            snap.docs.forEach((d) => {
              bucket[d.id] = d;
            });
            state.buckets[key] = bucket;
            emit();
          },
          (err) => {
            console.warn(`[timeline] ${s.collection} where(${field}==user) —`, err?.code, err?.message);
          }
        )
      );
    });
  });

  return () => unsubs.forEach((u) => u());
}

/** Deal timeline: deal activity + full merged timeline for linked contact (same as contact panel). */
export function watchDealMergedTimeline(dealId, contactUserId, callback, onError) {
  if (!dealId) return () => {};
  const state = { deal: [], contact: [] };
  const emit = () => {
    callback(mergeByTime([...state.deal, ...state.contact], 150));
  };
  const unsubs = [];
  unsubs.push(
    watchDealActivity(
      dealId,
      (rows) => {
        state.deal = rows;
        emit();
      },
      onError
    )
  );
  if (contactUserId) {
    unsubs.push(
      watchUserMergedTimeline(
        contactUserId,
        (rows) => {
          state.contact = rows;
          emit();
        },
        onError
      )
    );
  } else {
    emit();
  }
  return () => unsubs.forEach((u) => u());
}

const DEFAULT_RECENT_PER_SOURCE = 35;

/**
 * Team-wide activity: optional CRM `activity_feed` + latest rows from each product collection (all users).
 * @param {object} [opts]
 * @param {number} [opts.mergeCap] max rows after sort
 * @param {number} [opts.perSourceLimit] per collection for recent query
 * @param {boolean} [opts.includeActivityFeed] set false if `activity_feed` is missing or blocked (product-only feed)
 */
export function watchRecentProductActivity(callback, onError, opts = {}) {
  const mergeCap = opts.mergeCap ?? 60;
  const perSource = opts.perSourceLimit ?? DEFAULT_RECENT_PER_SOURCE;
  const includeActivityFeed = opts.includeActivityFeed !== false;

  const state = { crm: [] };
  PLATFORM_SOURCES.forEach((s) => {
    state[s.id] = [];
  });

  const onSourceErr = opts.onSourceError;

  const emit = () => {
    const plat = [];
    PLATFORM_SOURCES.forEach((s) => {
      plat.push(...(state[s.id] || []));
    });
    callback(mergeByTime([...state.crm, ...plat], mergeCap));
  };

  const unsubs = [];

  if (includeActivityFeed) {
    unsubs.push(
      watchGlobalActivity(
        (rows) => {
          state.crm = rows;
          emit();
        },
        onError
      )
    );
  }

  PLATFORM_SOURCES.forEach((s) => {
    unsubs.push(
      attachRecentCollectionListener(
        s,
        perSource,
        (rows) => {
          state[s.id] = rows;
          emit();
        },
        onSourceErr
      )
    );
  });

  return () => unsubs.forEach((u) => u());
}
